/**
 * PourLocationsSection - Manage monitoring locations for a pour
 *
 * UI mirrors PourCreate's Hardware / Sensors step (same table, same
 * "CREATE A NEW LOCATION" button, same hardware-type icon dialog) so the
 * create and edit flows read identically; rows here save immediately via
 * the pour-locations API instead of being staged.
 *
 * In edit mode the table is inline-editable like PourCreate's (name, device,
 * channel, start / concrete start / end times, tank); each cell change saves
 * that one field straight away. The pencil opens the full dialog for the
 * rest (targets, limits, differentials, functions).
 *
 * Adding a ConXagent shows all its channels at once (ChannelRowsPicker): the
 * user types a name per channel, and every ticked channel saves in one
 * all-or-nothing bulk request. "Save & add next device" keeps the dialog
 * open with the times and settings. When the pour has a
 * temperature differential limit, each ticked channel gets a "Differentials"
 * checklist of the other ticked channels and every location on the pour;
 * pairs between channels are remembered for the next device. A ConXsensor
 * has one channel, so it gets no channel picker.
 *
 * With a differential limit the table also gets a Differentials column: the
 * pairs of each location (read from both sides) as chips, and a click on a
 * concrete location's cell edits them in one save.
 *
 * Field visibility mirrors legacy C# Pour2LocationAdd:
 * - MonitorType controls target strength enable/disable
 * - hasWeather only shown if pour has WeatherStationID
 * - Target strengths only shown if pour has targets
 * - EAStart auto-synced from LogginStart
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  MenuItem,
  Alert,
  Paper,
  Card,
  CardContent,
  Chip,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Autocomplete,
  Checkbox,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  poursAPI,
  type PourLocation2,
  type PourLocation2Create,
  type PourLookups,
  type Pour2Target,
  type Pour2Limit,
  type TargetStrengthOverride,
} from '../../services/pours';
import { utcToLocalInput, localInputToUtc, formatInTimeZone, timeZoneLabel } from '../../utils/datetime';
import { describeMaturity } from '../../utils/maturityDisplay';
import {
  locationFunctionFields,
  locationFunctionLabel,
  locationFunctionOf,
  locationFunctionView,
  type LocationFunctionValue,
} from '../../utils/locationFunction';
import { pourComputesMaturity } from '../../utils/calibrationCheck';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin } from '../../utils/userPermissions';
import { apiErrorText } from '../../utils/apiError';
import {
  isDuplicateLocationName,
  isLoggerFullyUsed,
  isMultiChannelHardware,
  locationNameKey,
  type PourChannelRef,
} from '../../utils/channelRows';
import { BRAND_GREEN, PICKER_POPPER_SX } from './pourWizardStyles';
import { useChannelBatch } from './useChannelBatch';
import ChannelRowsPicker from './ChannelRowsPicker';
import DifferentialsCell, { type DiffPartnerOption } from './DifferentialsCell';
import HardwareNumberPicker, { type HardwareNumberOption } from './HardwareNumberPicker';
import HardwareTypeCards, { HardwareTypeImage } from './HardwareTypeCards';
import LocationFunctionToggle from './LocationFunctionToggle';
import {
  CONCRETE_START_SHORT_LABEL,
  DialogStep,
  HARDWARE_TYPE_NAMES,
  NOT_APPLICABLE,
  TABLE_ICON_CELL_SX,
  TABLE_TICK_HEAD_SX,
  TABLE_NAME_CELL_SX,
  TABLE_TIME_CELL_SX,
} from './locationTableParts';

// ---------------------------------------------------------------------------
// Props & form types
// ---------------------------------------------------------------------------

interface PourLocationsSectionProps {
  pourId: string;
  mode: 'create' | 'edit' | 'view';
  lookups: PourLookups;
  /** Pour-level context for conditional field visibility */
  pourContext?: {
    WeatherStationID?: string;
    TargetGroupID?: string;
    CalibrationID?: string;
    Functionality?: string;
    MaturityMethod?: string;
    /** Arrhenius specified temperature (°C; a decimal may arrive as text).
     *  Names the equivalent age ("Equivalent Age at 20 °C"); 20 °C when
     *  not given. */
    SpecifiedTemp?: number | string | null;
    /** Comma-joined monitoring functions — drives the dialog's Monitoring
     *  Functions checkboxes and the default hardware type, like PourCreate. */
    MonitoringFunctions?: string;
    /** Pour status label ('Pending' | 'In Progress' | ...). Changing a
     *  running channel's device / channel / times restarts its tracking, so
     *  edits to an In Progress pour ask first. */
    PourStatus?: string;
  };
  /** IANA TZ from the pour's project. Datetime fields read/write in this
   *  timezone so the user enters wall-clock time at the project site. */
  projectTimezoneIana?: string;
  /** The company strength unit (MPa / Psi / N/mm²) for the per-location
   *  target strengths; they show without a unit until it is known. */
  strengthUnit?: string | null;
}

interface LocationFormState {
  LocationID: string;
  LocationDescription: string;
  LoggerID: string;
  ChannelNo: string;
  MonitorType: string;
  /** Temperature Only (ambient): a concrete-hardware channel reading the air. */
  IsAmbient: boolean;
  LogginStart: string;
  EAStart: string;
  /** Monitoring stop time (UTC ISO, '' = open-ended). */
  LoggingEnd: string;
  hasWeather: boolean;
  /** Tank serial number bound to this location ('' = unbound). */
  TankID: string;
  /** Per-target strength overrides, keyed by Pour2Target.ID */
  targetStrengths: Record<string, string>;
  /** Apply pour temperature limit (Pour2DefaultLimits Criteria='02') to this location */
  hasTempLimit: boolean;
  /** Other PourLocation IDs to apply temp-differential limit against (Criteria='01') */
  tempDiffLocationIds: string[];
}

const EMPTY_FORM: LocationFormState = {
  LocationID: '',
  LocationDescription: '',
  LoggerID: '',
  ChannelNo: '',
  MonitorType: '1',
  IsAmbient: false,
  LogginStart: '',
  EAStart: '',
  LoggingEnd: '',
  hasWeather: false,
  TankID: '',
  targetStrengths: {},
  hasTempLimit: false,
  tempDiffLocationIds: [],
};

// ---------------------------------------------------------------------------
// Hardware types (mirrors PourCreate)
// ---------------------------------------------------------------------------

type HardwareType = 'ConXagent' | 'ConXsensors' | 'ConXone' | 'Weather Station' | 'Temperature & Humidity' | 'TMC' | '';

const HARDWARE_TYPE_OPTIONS: { value: HardwareType; label: string }[] = [
  { value: 'ConXagent', label: 'ConXagent' },
  { value: 'ConXsensors', label: 'ConXsensor' },
  { value: 'ConXone', label: 'ConXone' },
  { value: 'TMC', label: 'Match\nTank' },
  { value: 'Weather Station', label: 'Weather\nStation' },
  { value: 'Temperature & Humidity', label: 'Temp &\nHumidity' },
];

const HARDWARE_TYPE_TO_LOGGER_TYPE: Record<HardwareType, string[]> = {
  'ConXagent': ['01'],
  'ConXsensors': ['02'],
  'ConXone': ['03'],
  'Weather Station': ['07'],
  'Temperature & Humidity': ['09', '10'],
  'TMC': ['08'],
  '': [],
};

// Which hardware types each monitoring function works with. A type is offered
// when ANY of the pour's functions allows it; legacy pours with no functions
// string see every type. Mirrors PourCreate.
const FUNCTION_HARDWARE: Record<string, HardwareType[]> = {
  'Temperature Only': ['ConXagent', 'ConXsensors', 'ConXone'],
  'Concrete Maturity': ['ConXagent', 'ConXsensors', 'ConXone'],
  'Temperature Match Curing': ['ConXagent', 'ConXsensors', 'ConXone', 'TMC'],
  'Calculate Concrete Evaporation Rate': ['ConXagent', 'ConXsensors', 'ConXone', 'Weather Station'],
  'Onsite Weather Conditions': ['Weather Station'],
  'External Temperature and Humidity': ['Temperature & Humidity'],
};

const allowedHardwareTypes = (functions: string[]): Set<HardwareType> => {
  if (!functions.length) {
    return new Set(HARDWARE_TYPE_OPTIONS.map((o) => o.value));
  }
  const allowed = new Set<HardwareType>();
  functions.forEach((f) => (FUNCTION_HARDWARE[f] ?? []).forEach((h) => allowed.add(h)));
  return allowed;
};

/** Hardware types whose devices carry a selectable channel number. */
const HW_HAS_CHANNEL: Record<HardwareType, boolean> = {
  'ConXagent': true,
  'ConXsensors': true,
  'ConXone': true,
  'Weather Station': false,
  'Temperature & Humidity': false,
  'TMC': true,
  '': true,
};

/** Hardware types with a single channel: no channel to pick, always 1. */
const HW_SINGLE_CHANNEL: Record<HardwareType, boolean> = {
  'ConXagent': false,
  'ConXsensors': true,
  'ConXone': true,
  'Weather Station': false,
  'Temperature & Humidity': false,
  'TMC': false,
  '': false,
};

const hwFromLoggerType = (type?: string | null, tankId?: string | null): HardwareType => {
  if (tankId) return 'TMC';
  if (type === '02') return 'ConXsensors';
  if (type === '03') return 'ConXone';
  if (type === '07') return 'Weather Station';
  if (type === '08') return 'TMC';
  if (type === '09' || type === '10') return 'Temperature & Humidity';
  return 'ConXagent';
};

const MONITOR_TYPE_TEMP_ONLY = '2';
const MONITOR_TYPE_EA = '1';

// Type "99" is the simulator's virtual logger (see backend pour.py /simulate).
// Super admins can assign it under any hardware type to drive simulated data.
const VIRTUAL_LOGGER_TYPE = '99';

const loggerDisplayName = (l: { name: string; type?: string | null } | null | undefined): string | undefined =>
  l ? (l.type === VIRTUAL_LOGGER_TYPE ? 'Virtual Logger (Simulator)' : l.name) : undefined;


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PourLocationsSection: React.FC<PourLocationsSectionProps> = ({
  pourId,
  mode,
  lookups,
  pourContext,
  projectTimezoneIana,
  strengthUnit,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isViewMode = mode === 'view';
  // The concrete start (EAStart) has one name on the pour pages, with what
  // it does (same keys as the create wizard).
  const concreteStartLabel = t('pourLocations.concreteStart', 'Concrete start (maturity counted from)');
  const concreteStartHelp = t(
    'pourLocations.concreteStartHelp',
    'Maturity is counted from this time. Set it to when the concrete was placed around the sensor.',
  );

  const [locations, setLocations] = useState<PourLocation2[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<PourLocation2 | null>(null);
  const [formState, setFormState] = useState<LocationFormState>(EMPTY_FORM);
  const [hwType, setHwType] = useState<HardwareType>('');
  const [saving, setSaving] = useState(false);
  /** The dialog's validation list shows from the first Save that finds a
   *  problem until the dialog is opened again, like PourCreate (see
   *  shownDialogErrors). */
  const [dialogErrorsShown, setDialogErrorsShown] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<PourLocation2 | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Pour-level targets for the strength editing section
  const [pourTargets, setPourTargets] = useState<Pour2Target[]>([]);

  // Pour-level default limits drive the temp-limit checkbox and temp-diff
  // chooser visibility (Pour2DefaultLimits Criteria '02' / '01').
  const [pourLimits, setPourLimits] = useState<Pour2Limit[]>([]);

  // Inline table editing: the row being saved, in-progress name edits
  // (keyed by row ID) and the date picker opened from a time cell.
  const [rowSavingId, setRowSavingId] = useState<string | null>(null);
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const [datePopover, setDatePopover] = useState<{
    anchorEl: HTMLElement;
    loc: PourLocation2;
    field: 'LogginStart' | 'EAStart' | 'LoggingEnd';
    value: Dayjs | null;
  } | null>(null);

  // "Save & add next device": the note after each add, how many locations
  // this dialog session has added (Cancel becomes Close), and the inputs it
  // moves focus to.
  const [addNotice, setAddNotice] = useState<string | null>(null);
  const [addedCount, setAddedCount] = useState(0);
  const deviceInputRef = useRef<HTMLInputElement>(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  /** The dialog's notes and errors sit at the top of its content. */
  const scrollDialogToTop = () => {
    if (dialogContentRef.current) dialogContentRef.current.scrollTop = 0;
  };

  // Adding a ConXagent: every channel at once, a name per channel.
  // Editing an existing location always stays a single-location form.
  const multiMode = !editingLocation && isMultiChannelHardware(hwType);
  const pourChannels = useMemo<PourChannelRef[]>(
    () => locations.map((l) => ({ loggerId: l.LoggerID, channelNo: l.ChannelNo, name: l.LocationID })),
    [locations],
  );
  // Concrete locations choose Temperature Only or Concrete Maturity, each
  // channel on its own, when the pour has maturity (legacy: per location).
  // A legacy pour without a Functionality code counts as a maturity pour,
  // as the rest of this section treats it.
  const maturityAllowed = pourComputesMaturity(
    pourContext?.MonitoringFunctions,
    pourContext?.Functionality ?? '1',
  );
  const batch = useChannelBatch({
    loggerId: formState.LoggerID,
    pourChannels,
    nameScope: hwType,
    maturityAllowed,
  });

  // Device, channel, start/end and maturity changes restart the channel's
  // tracking on the backend (legacy ChannelReStart): its maturity and limit
  // tracking is rebuilt from the new settings. Confirm first on a live pour.
  const pourRunning = (pourContext?.PourStatus ?? '').toLowerCase() === 'in progress';
  const confirmRestart = (message?: string): boolean =>
    !pourRunning ||
    window.confirm(
      message ||
        t(
          'pourLocations.restartConfirm',
          'This pour is in progress. Changing the device, channel, start or end time restarts tracking for this location (maturity and limit tracking are recalculated). Continue?',
        ),
    );

  // Derived visibility flags (hasWeatherStation: below, from the locations)
  const targetGroupId = pourContext?.TargetGroupID ?? '';
  const hasTargetGroup = Boolean(targetGroupId);

  // Pour monitoring functions — drives the dialog checkboxes and the default
  // hardware type, mirroring PourCreate's openAddLocation.
  const pourFunctions = useMemo(
    () =>
      (pourContext?.MonitoringFunctions || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [pourContext?.MonitoringFunctions],
  );

  // Only available tanks (not held by another active pour); the currently
  // assigned tank stays visible when editing.
  const sortedTanks = useMemo(
    () =>
      [...(lookups.tanks ?? [])]
        .filter(
          (tk) =>
            !tk.in_use_by_pour ||
            tk.in_use_by_pour === pourId ||
            tk.id === formState.TankID,
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [lookups.tanks, pourId, formState.TankID],
  );

  // Hardware types this pour's monitoring functions support (all types for
  // legacy pours without a functions string). When editing a location whose
  // hardware wouldn't be allowed today, its type stays visible so the dialog
  // can still render the existing selection.
  const hardwareOptions = useMemo(() => {
    const allowed = allowedHardwareTypes(pourFunctions);
    if (editingLocation && hwType) allowed.add(hwType);
    return HARDWARE_TYPE_OPTIONS.filter((o) => allowed.has(o.value));
  }, [pourFunctions, editingLocation, hwType]);

  const loggerTypeById = useMemo(
    () => new Map(lookups.loggers.map((l) => [l.id, l.type])),
    [lookups.loggers],
  );

  // Weather stations ('07') on the pour. The server makes one of them the
  // pour's weather station (WeatherStationID) once the pour uses weather,
  // and the weather job feeds only that one. pourContext is read when the
  // page loads, so a station added since then counts here too: the
  // Evaporation column shows without a reload.
  const stationLocations = useMemo(
    () => locations.filter((l) => loggerTypeById.get(l.LoggerID) === '07'),
    [locations, loggerTypeById],
  );
  const usesWeather =
    pourFunctions.includes('Onsite Weather Conditions') ||
    pourFunctions.includes('Calculate Concrete Evaporation Rate');
  const linkedStation =
    stationLocations.find((l) => l.LoggerID === pourContext?.WeatherStationID) ??
    (usesWeather ? stationLocations[0] : undefined);
  const hasWeatherStation = Boolean(pourContext?.WeatherStationID) || Boolean(linkedStation);
  const stationName = (loc: PourLocation2) => loc.LoggerCode || loc.LocationID || loc.LoggerID;

  // The maturity function, named by the backend rule: only the exact method
  // "Nurse Saul" is a Temperature-Time Factor; every other value is
  // calculated as an Equivalent Age at the pour's specified temperature.
  const functionLabel = describeMaturity(
    pourContext?.MaturityMethod,
    pourContext?.SpecifiedTemp,
  ).index_label;

  // =====================================================================
  // Data loading
  // =====================================================================

  const loadLocations = useCallback(async () => {
    if (!pourId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await poursAPI.getPourLocations2(pourId);
      setLocations(data);
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to load locations'));
    } finally {
      setLoading(false);
    }
  }, [pourId]);

  const loadPourTargets = useCallback(async () => {
    if (!pourId) return;
    if (!hasTargetGroup) {
      setPourTargets([]);
      return;
    }
    try {
      const targets = await poursAPI.getPourTargets(pourId);
      setPourTargets(targets);
    } catch {
      // Non-critical — targets section just won't show
    }
    // Depend on targetGroupId (not just hasTargetGroup boolean) so switching
    // from one non-empty group to another re-fetches the new group's targets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pourId, hasTargetGroup, targetGroupId]);

  const loadPourLimits = useCallback(async () => {
    if (!pourId) return;
    try {
      const limits = await poursAPI.getPourLimits(pourId);
      setPourLimits(limits);
    } catch {
      setPourLimits([]);
    }
  }, [pourId]);

  useEffect(() => {
    loadLocations();
    loadPourTargets();
    loadPourLimits();
  }, [loadLocations, loadPourTargets, loadPourLimits]);

  const hasTempLimitDefault = useMemo(
    () => pourLimits.some((l) => l.Criteria === '02'),
    [pourLimits],
  );
  const hasTempDiffLimitDefault = useMemo(
    () => pourLimits.some((l) => l.Criteria === '01'),
    [pourLimits],
  );

  // =====================================================================
  // Dialog handlers
  // =====================================================================

  const handleOpenAdd = async () => {
    setEditingLocation(null);
    // Refresh pour targets so the dialog reflects the latest target group.
    await loadPourTargets();
    const defaultStrengths: Record<string, string> = {};
    pourTargets.forEach((t) => {
      defaultStrengths[t.ID] = t.Strength != null ? String(t.Strength) : '';
    });
    // Default hardware type from the pour's functions (same as PourCreate),
    // clamped to the types those functions actually support.
    const allowed = allowedHardwareTypes(pourFunctions);
    let defaultHW: HardwareType = 'ConXagent';
    if (pourFunctions.includes('Temperature Match Curing')) defaultHW = 'TMC';
    else if (pourFunctions.includes('Onsite Weather Conditions')) defaultHW = 'Weather Station';
    else if (pourFunctions.includes('External Temperature and Humidity')) defaultHW = 'Temperature & Humidity';
    if (!allowed.has(defaultHW)) {
      defaultHW =
        HARDWARE_TYPE_OPTIONS.map((o) => o.value).find((v) => allowed.has(v)) ?? '';
    }
    setHwType(defaultHW);
    setFormState({
      ...EMPTY_FORM,
      MonitorType: maturityAllowed ? MONITOR_TYPE_EA : MONITOR_TYPE_TEMP_ONLY,
      IsAmbient: false,
      targetStrengths: defaultStrengths,
    });
    setDialogErrorsShown(false);
    setError(null);
    // Fresh names, ticks and pour partners; channel pairs stay.
    batch.reset();
    setAddNotice(null);
    setAddedCount(0);
    setDialogOpen(true);
  };

  const handleOpenEdit = async (location: PourLocation2) => {
    setEditingLocation(location);

    // Refresh pour targets so the dialog reflects the latest target group.
    await loadPourTargets();

    // Load per-location target strengths (not pour-level defaults)
    let strengths: Record<string, string> = {};
    try {
      const locTargets = await poursAPI.getLocationTargets(pourId, location.ID);
      locTargets.forEach((lt) => {
        strengths[lt.pour2_target_id] = lt.strength != null ? String(lt.strength) : '';
      });
    } catch {
      // Fall back to pour-level defaults
      pourTargets.forEach((t) => {
        strengths[t.ID] = t.Strength != null ? String(t.Strength) : '';
      });
    }

    setHwType(hwFromLoggerType(loggerTypeById.get(location.LoggerID), location.TankID));
    setFormState({
      LocationID: location.LocationID || '',
      LocationDescription: location.LocationDescription || '',
      LoggerID: location.LoggerID || '',
      ChannelNo: location.ChannelNo != null ? String(location.ChannelNo) : '',
      MonitorType: maturityAllowed
        ? (location.MonitorType || MONITOR_TYPE_EA)
        : MONITOR_TYPE_TEMP_ONLY,
      IsAmbient: Boolean(location.IsAmbient),
      LogginStart: location.LogginStart ? location.LogginStart.slice(0, 16) : '',
      EAStart: location.EAStart ? location.EAStart.slice(0, 16) : '',
      LoggingEnd: location.LoggingEnd ? location.LoggingEnd.slice(0, 16) : '',
      hasWeather: location.hasWeather || false,
      TankID: location.TankID ?? '',
      targetStrengths: strengths,
      hasTempLimit: Boolean(location.has_temp_limit),
      tempDiffLocationIds: location.temp_diff_location_ids ?? [],
    });
    setDialogErrorsShown(false);
    setError(null);
    setAddNotice(null);
    setAddedCount(0);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogErrorsShown(false);
    setAddNotice(null);
    setEditingLocation(null);
    setFormState(EMPTY_FORM);
    setHwType('');
  };

  const handleFormChange = (field: keyof LocationFormState, value: any) => {
    setFormState((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'LogginStart') {
        // Auto-sync EAStart when LogginStart changes (legacy behavior): an
        // empty Concrete Start takes the new Start. When adding, a shown
        // Concrete Start that still matches the old Start follows it too,
        // so moving Start doesn't leave Concrete Start behind. Editing keeps
        // the legacy rule only.
        const concreteStartShown = prev.MonitorType === MONITOR_TYPE_EA && HW_HAS_CHANNEL[hwType];
        const followsStart =
          !editingLocation &&
          concreteStartShown &&
          !!prev.EAStart &&
          prev.EAStart.slice(0, 16) === (prev.LogginStart || '').slice(0, 16);
        if (!prev.EAStart || followsStart) {
          next.EAStart = value;
        }
      }
      return next;
    });
  };

  const handleTargetStrengthChange = (targetId: string, value: string) => {
    setFormState((prev) => ({
      ...prev,
      targetStrengths: { ...prev.targetStrengths, [targetId]: value },
    }));
  };

  /** What stops the dialog saving, from the form and channel rows as they
   *  are now. It sets no state, so the list can be worked out again on
   *  every render (shownDialogErrors). */
  const dialogProblems = (): string[] => {
    const errors: string[] = [];
    // In multi mode each channel is named in the picker, not Location Name.
    if (!multiMode && !formState.LocationID.trim()) errors.push('Location Name is required.');
    if (!hwType) errors.push('Hardware Type is required.');
    if (!formState.LoggerID) errors.push('Hardware Number is required.');
    if (!formState.LogginStart) errors.push('Monitoring Start Time is required.');
    if (concreteStartVisible && !formState.EAStart) {
      errors.push(`${concreteStartLabel} is required when Maturity is enabled.`);
    }
    if (hwType === 'TMC' && !formState.TankID.trim()) {
      errors.push('Tank is required for Temperature Match Curing.');
    }
    if (multiMode) {
      errors.push(...batch.errors);
    } else if (formState.LocationID.trim()) {
      // The server refuses a name already on this pour; say so before
      // sending. An edit that keeps its name is never blocked, so existing
      // legacy duplicates can still be edited.
      const nameChanged =
        !editingLocation ||
        locationNameKey(formState.LocationID) !== locationNameKey(editingLocation.LocationID);
      const otherNames = locations
        .filter((l) => l.ID !== editingLocation?.ID)
        .map((l) => l.LocationID);
      const newNames =
        enviroExpandChannels.length > 0
          ? enviroExpandNames(formState.LocationID.trim()).map((e) => e.name)
          : [formState.LocationID.trim()];
      if (nameChanged) {
        newNames
          .filter((n) => isDuplicateLocationName(n, otherNames))
          .forEach((n) => errors.push(`A location named "${n}" is already on this pour.`));
      }
    }
    return errors;
  };

  /** Save the dialog. With addNext (add mode only) the dialog stays open for
   *  the next device, keeping the times and settings. */
  const handleSave = async (addNext = false) => {
    const errors = dialogProblems();
    // Each channel row shows its own error from the first Save on, too.
    if (multiMode) batch.validate();
    if (errors.length > 0) {
      setDialogErrorsShown(true);
      setAddNotice(null);
      // The messages sit at the top of the dialog; bring them into view.
      scrollDialogToTop();
      return;
    }
    setDialogErrorsShown(false);

    // The dialog works to the minute; stored times may carry seconds.
    const sameTime = (a?: string | null, b?: string | null) =>
      (a || '').slice(0, 16) === (b || '').slice(0, 16);
    /** On an edit, a time is sent only when the user changed it. */
    const timeUnchanged = (field: 'LogginStart' | 'EAStart' | 'LoggingEnd') =>
      !!editingLocation && sameTime(formState[field], editingLocation[field]);

    if (editingLocation) {
      const restarts =
        formState.LoggerID !== editingLocation.LoggerID ||
        parseInt(formState.ChannelNo, 10) !== editingLocation.ChannelNo ||
        !sameTime(formState.LogginStart, editingLocation.LogginStart) ||
        !sameTime(formState.LoggingEnd, editingLocation.LoggingEnd) ||
        (formState.MonitorType || '') !== (editingLocation.MonitorType || '');
      // Limits, differentials and evaporation are set in the table, not the
      // dialog, so an edit here never changes them. A target strength change
      // does not restart: it only moves that target's maturity threshold.
      if (restarts && !confirmRestart()) return;
    }

    // Build target strength overrides from form
    const targetOverrides: TargetStrengthOverride[] = Object.entries(formState.targetStrengths)
      .filter(([, val]) => val !== '')
      .map(([targetId, val]) => ({
        pour2_target_id: targetId,
        strength: parseFloat(val),
      }));

    const payload: PourLocation2Create = {
      LocationID: formState.LocationID.trim(),
      LocationDescription:
        (formState.LocationDescription || formState.LocationID).trim() || undefined,
      LoggerID: formState.LoggerID,
      ChannelNo: parseInt(formState.ChannelNo, 10),
      StartType: '2', // Hardcoded to "Start at Date/Time" (legacy behavior)
      // A new weather station or ambient temperature & humidity location
      // measures the site, not the concrete: it never computes maturity
      // (as in the create wizard). An existing row keeps its own.
      MonitorType:
        isSiteHardware && !editingLocation
          ? MONITOR_TYPE_TEMP_ONLY
          : formState.MonitorType || undefined,
      // Site hardware is ambient by its type; the flag is for concrete
      // hardware only.
      IsAmbient: isSiteHardware ? undefined : formState.IsAmbient,
      // formState.LogginStart / EAStart already hold UTC ISO strings produced
      // by `localInputToUtc` on each onChange — re-wrapping with `new Date()`
      // would re-parse them as browser-local and shift by the TZ offset.
      // An edit sends the start, concrete start and stop only when they were
      // changed: a stored time with seconds sent back cut to the minute would
      // otherwise read as a new time (a new start restarts the channel).
      LogginStart: timeUnchanged('LogginStart') ? undefined : formState.LogginStart || undefined,
      EAStart: timeUnchanged('EAStart') ? undefined : formState.EAStart || undefined,
      // On update an explicit null clears a previously set stop time;
      // undefined leaves the stored value untouched.
      LoggingEnd: timeUnchanged('LoggingEnd')
        ? undefined
        : formState.LoggingEnd || (editingLocation ? null : undefined),
      // Evaporation, the temperature limit and differentials are switched on
      // in the table after adding; an edit leaves them as they are.
      hasWeather: hasWeatherStation && !editingLocation ? formState.hasWeather : undefined,
      // Always send TankID so unbinding (empty string) round-trips through
      // the backend's swap logic. Omitting it leaves the binding untouched.
      TankID: hwType === 'TMC' ? formState.TankID : '',
      target_strengths: targetOverrides.length > 0 ? targetOverrides : undefined,
      // Site hardware never owns a limit or a pair; its hidden choosers may
      // still hold ticks from an earlier device, so send none.
      has_temp_limit: hasTempLimitDefault && !editingLocation
        ? (isSiteHardware || formState.IsAmbient ? false : formState.hasTempLimit)
        : undefined,
      temp_diff_location_ids: hasTempDiffLimitDefault && !editingLocation
        ? (isSiteHardware ? [] : formState.tempDiffLocationIds)
        : undefined,
    };

    // Several locations from one entry go in a single bulk request, so a
    // failure saves none of them. A ConXagent adds every ticked channel,
    // each under its own name. Both are add-only.
    let bulkRows: PourLocation2Create[] = [];
    if (multiMode) {
      // Each differential picked between two channels of this device goes on
      // its lower channel's row as the final name of the higher channel; a
      // row's pairs with locations already on the pour go as their IDs. The
      // server resolves both in the same request and stores each pair once.
      const partnerNamesFor = (no: number): string[] => [
        ...batch.differentialPairs
          .filter((p) => p.a === no)
          .map((p) => batch.selected.find((r) => r.no === p.b)?.finalName || '')
          .filter(Boolean),
        ...batch.pourPartnersFor(no).filter((id) => locations.some((l) => l.ID === id)),
      ];
      // Each channel has its own Function: a Temperature Only channel has
      // no concrete start and no strength targets, and one reading the air
      // (ambient) no temperature limit either.
      bulkRows = batch.selected.map((r) => ({
        ...payload,
        LocationID: r.finalName,
        LocationDescription: r.finalName,
        ChannelNo: r.no,
        ...locationFunctionFields(r.fn),
        EAStart: r.maturity ? payload.EAStart : undefined,
        target_strengths: r.maturity ? payload.target_strengths : undefined,
        has_temp_limit: r.fn === 'ambient' && hasTempLimitDefault ? false : payload.has_temp_limit,
        temp_diff_location_ids: hasTempDiffLimitDefault ? partnerNamesFor(r.no) : undefined,
      }));
    } else if (enviroExpandChannels.length > 0) {
      // ConXenviro: expand the single entry into one location per real
      // sensor channel, the same names the dialog previews (mirrors
      // PourCreate's saveLocation). Humidity channels never carry
      // maturity, temp limits, evaporation or differential pairings.
      bulkRows = enviroExpandNames(formState.LocationID.trim()).map(({ no, name }) => {
        const isHumidity =
          enviroExpandChannels.find((c) => c.no === no)?.type === 'HUMIDITY';
        return {
          ...payload,
          LocationID: name,
          LocationDescription: name,
          ChannelNo: no,
          ...(isHumidity
            ? {
                MonitorType: MONITOR_TYPE_TEMP_ONLY,
                hasWeather: hasWeatherStation ? false : undefined,
                has_temp_limit: hasTempLimitDefault ? false : undefined,
                temp_diff_location_ids: hasTempDiffLimitDefault ? [] : undefined,
              }
            : {}),
        };
      });
    }

    setSaving(true);
    setError(null);
    try {
      if (editingLocation) {
        await poursAPI.updatePourLocation2(pourId, editingLocation.ID, payload);
        handleCloseDialog();
        await loadLocations();
        return;
      }
      const created: PourLocation2[] =
        bulkRows.length > 0
          ? await poursAPI.addPourLocations2Bulk(pourId, bulkRows)
          : [await poursAPI.addPourLocation2(pourId, payload)];
      if (!addNext) {
        handleCloseDialog();
        await loadLocations();
        return;
      }
      // Save & add next device: merge the new rows straight away so their
      // channels grey out and a full device drops off the list, then
      // refresh from the server in the background.
      const newRows = (created || []).filter(Boolean);
      setLocations((prev) => [...prev, ...newRows.filter((c) => !prev.some((p) => p.ID === c.ID))]);
      const count = bulkRows.length || 1;
      const device = loggerDisplayName(dialogLogger) || formState.LoggerID;
      setAddNotice(`Added ${count} location${count === 1 ? '' : 's'} for ${device}`);
      setAddedCount((n) => n + count);
      setFormState((prev) => ({
        ...prev,
        LoggerID: '',
        ChannelNo: '',
        LocationID: '',
        LocationDescription: '',
        TankID: '',
        // Partners picked for the last device don't apply to the next one.
        tempDiffLocationIds: [],
      }));
      batch.resetForNextDevice();
      loadLocations();
      scrollDialogToTop();
      // Not on a phone: the keyboard would pop up over the note.
      if (!isMobile) deviceInputRef.current?.focus();
    } catch (err: any) {
      // Keep every entry so the user can fix the problem and save again.
      // The message shows at the top of the dialog, so scroll up to it.
      setAddNotice(null);
      setError(apiErrorText(err, 'Failed to save location'));
      scrollDialogToTop();
    } finally {
      setSaving(false);
    }
  };

  // =====================================================================
  // Delete handlers
  // =====================================================================

  const handleDeleteClick = (location: PourLocation2) => {
    setLocationToDelete(location);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!locationToDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await poursAPI.deletePourLocation2(pourId, locationToDelete.ID);
      setDeleteConfirmOpen(false);
      setLocationToDelete(null);
      // Deleting from inside the edit dialog: the row is gone, so close it.
      if (editingLocation?.ID === locationToDelete.ID) handleCloseDialog();
      await loadLocations();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to delete location'));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setLocationToDelete(null);
  };

  // =====================================================================
  // Inline (table) editing
  // =====================================================================

  const saveRowPatch = async (
    loc: PourLocation2,
    patch: Partial<PourLocation2Create>,
    restarts: boolean,
    /** Live-pour question for this change (defaults to the device / time one). */
    restartMessage?: string,
  ): Promise<void> => {
    if (restarts && !confirmRestart(restartMessage)) return;
    setRowSavingId(loc.ID);
    setError(null);
    try {
      await poursAPI.updatePourLocation2(pourId, loc.ID, patch);
      await loadLocations();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to save location'));
    } finally {
      setRowSavingId(null);
    }
  };

  const dropNameDraft = (locId: string) =>
    setNameDrafts((prev) => {
      const next = { ...prev };
      delete next[locId];
      return next;
    });

  const commitNameDraft = (loc: PourLocation2) => {
    const draft = nameDrafts[loc.ID];
    if (draft === undefined) return;
    dropNameDraft(loc.ID);
    const name = draft.trim();
    if (!name || name === loc.LocationID) return;
    // The description mirrors the name unless it was set to something else.
    const descFollowsName = !loc.LocationDescription || loc.LocationDescription === loc.LocationID;
    saveRowPatch(
      loc,
      descFollowsName ? { LocationID: name, LocationDescription: name } : { LocationID: name },
      false,
    );
  };

  const commitDate = (val: Dayjs | null) => {
    if (!datePopover) return;
    const { loc, field } = datePopover;
    setDatePopover(null);
    if (!val || !val.isValid()) return;
    const utc = localInputToUtc(val.format('YYYY-MM-DDTHH:mm'), projectTimezoneIana);
    const current = loc[field];
    if (current && current.slice(0, 16) === utc.slice(0, 16)) return;
    // Concrete Start (EAStart) doesn't restart the channel on the backend.
    saveRowPatch(loc, { [field]: utc }, field !== 'EAStart');
  };

  // =====================================================================
  // Helpers
  // =====================================================================

  const getLoggerName = (loggerId: string): string => {
    const found = lookups.loggers.find((l) => l.id === loggerId);
    return found ? found.name : loggerId;
  };

  const getTankName = (tankId?: string | null): string => {
    if (!tankId) return '';
    return lookups.tanks?.find((tk) => tk.id === tankId)?.name || tankId;
  };

  // Devices offered in the Hardware Number dropdown, filtered by the selected
  // hardware type (same mapping as PourCreate; untyped devices always pass)
  // and by availability — hardware held by another active pour is hidden.
  // When adding, a device with every channel already on this pour has
  // nothing left to add, so it is hidden too. The location's currently
  // assigned logger always stays visible.
  const hideFullyUsed = !editingLocation && HW_HAS_CHANNEL[hwType];
  const filteredLoggers = useMemo(() => {
    const allowedTypes = HARDWARE_TYPE_TO_LOGGER_TYPE[hwType];
    return [...lookups.loggers]
      .filter((l) => {
        if (l.id === formState.LoggerID) return true;
        if (l.in_use_by_pour && l.in_use_by_pour !== pourId) return false;
        if (hideFullyUsed && isLoggerFullyUsed(pourChannels, l.id, HW_SINGLE_CHANNEL[hwType] ? [1] : undefined)) {
          return false;
        }
        if (l.type === VIRTUAL_LOGGER_TYPE) return superAdmin;
        if (!allowedTypes || allowedTypes.length === 0) return true;
        if (!l.type) return true;
        return allowedTypes.includes(l.type);
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [lookups.loggers, hwType, pourId, formState.LoggerID, superAdmin, hideFullyUsed, pourChannels]);

  /** Hardware Number picker options. A selected device missing from the
   *  lookups (e.g. no longer listed) is kept so the box doesn't show empty. */
  const deviceOptions = useMemo<HardwareNumberOption[]>(() => {
    const opts = filteredLoggers.map((l) => ({ id: l.id, label: loggerDisplayName(l) || l.name }));
    if (formState.LoggerID && !opts.some((o) => o.id === formState.LoggerID)) {
      opts.unshift({
        id: formState.LoggerID,
        label: editingLocation?.LoggerCode || formState.LoggerID,
      });
    }
    return opts;
  }, [filteredLoggers, formState.LoggerID, editingLocation]);

  /** Device picked in the dialog: start on its first free channel (single
   *  mode, channel 1 on a single-channel device); in multi mode move on to
   *  the first channel's name. */
  const handleDeviceChange = (newLoggerId: string) => {
    const usedChannels = locations
      .filter((loc) => loc.LoggerID === newLoggerId && loc.ID !== editingLocation?.ID)
      .map((loc) => loc.ChannelNo);
    const nextChannel = [1, 2, 3, 4].find((ch) => !usedChannels.includes(ch)) ?? 1;
    setFormState((prev) => ({
      ...prev,
      LoggerID: newLoggerId,
      ChannelNo: newLoggerId ? (channelVisible && !HW_SINGLE_CHANNEL[hwType] ? String(nextChannel) : '1') : '',
    }));
    if (multiMode && newLoggerId) {
      // After the picker has finished closing its list.
      setTimeout(() => firstNameInputRef.current?.focus(), 0);
    }
  };

  const channelVisible = HW_HAS_CHANNEL[hwType];
  // A ConXagent add has a Function per channel: the concrete start and the
  // targets apply when any ticked channel is Concrete Maturity.
  const maturityChecked = multiMode
    ? batch.selected.some((r) => r.maturity)
    : formState.MonitorType === MONITOR_TYPE_EA;
  const concreteStartVisible = maturityChecked && channelVisible;
  const functionView = locationFunctionView(hwType, maturityAllowed);

  // Site-conditions hardware measures the environment, not the concrete —
  // temperature limits and differentials never apply to its own locations.
  const isSiteHardware = hwType === 'Temperature & Humidity' || hwType === 'Weather Station';
  // Differentials are directional: a concrete location MAY pair against an
  // ambient TEMPERATURE channel (core vs ambient is a real mass-concrete
  // criterion). Weather stations (no track data) and humidity channels never
  // qualify as partners.
  const canBeDiffPartner = (loc: PourLocation2): boolean =>
    loggerTypeById.get(loc.LoggerID) !== '07' &&
    (loc.channel_type || '').toUpperCase() !== 'HUMIDITY';

  // ─── Differentials column (table and mobile cards) ───
  // Only concrete locations set differentials: not site hardware (weather
  // station '07', ambient T&H '09' / '10') and not a humidity channel. Site
  // rows still show the pairs concrete locations made with them, read-only.
  const isConcreteLocation = (loc: PourLocation2): boolean => {
    const type = loggerTypeById.get(loc.LoggerID);
    if (type === '07' || type === '09' || type === '10') return false;
    // A channel reading the air (Temperature Only (ambient)) is site too.
    if (loc.IsAmbient) return false;
    return (loc.channel_type || '').toUpperCase() !== 'HUMIDITY';
  };
  const locationById = useMemo(() => new Map(locations.map((l) => [l.ID, l])), [locations]);
  const diffLabel = (loc: PourLocation2): string =>
    loc.LocationID || t('pourLocations.unnamedLocation', 'Unnamed Location');
  /** A location's partners, from the API's list (it holds both sides of each
   *  pair). Ids that are not on the loaded list are skipped. */
  const diffPartnersOf = (loc: PourLocation2): DiffPartnerOption[] => {
    const seen = new Set<string>();
    const partners: DiffPartnerOption[] = [];
    (loc.temp_diff_location_ids ?? []).forEach((id) => {
      const other = locationById.get(id);
      if (!other || other.ID === loc.ID || seen.has(id)) return;
      seen.add(id);
      partners.push({ id, label: diffLabel(other) });
    });
    return partners;
  };
  /** Every other location a concrete location may pair with. */
  const diffOptionsFor = (loc: PourLocation2): DiffPartnerOption[] =>
    locations
      .filter((o) => o.ID !== loc.ID && canBeDiffPartner(o))
      .map((o) => ({ id: o.ID, label: diffLabel(o) }));
  /** What a new ConXagent channel may pair with on the pour. */
  const pourDiffOptions = useMemo<DiffPartnerOption[]>(
    () => locations.filter(canBeDiffPartner).map((o) => ({ id: o.ID, label: diffLabel(o) })),
    // canBeDiffPartner and diffLabel only read locations' logger types and t.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locations, loggerTypeById],
  );
  /** One location's differentials in the table or a card. The new list
   *  replaces every pair touching the location, in one save. A changed list
   *  restarts the location's tracking, so a live pour asks first. */
  const renderDiffCell = (loc: PourLocation2, variant: 'cell' | 'inline') => {
    const concrete = isConcreteLocation(loc);
    return (
      <DifferentialsCell
        partners={diffPartnersOf(loc)}
        options={concrete ? diffOptionsFor(loc) : []}
        editable={!isViewMode && concrete}
        disabled={rowSavingId === loc.ID}
        emptyText={concrete ? 'None' : '-'}
        variant={variant}
        onChange={(nextIds) =>
          saveRowPatch(
            loc,
            { temp_diff_location_ids: nextIds },
            true,
            t(
              'pourLocations.restartConfirmDiff',
              'This pour is in progress. Changing the temperature differentials restarts tracking for this location (maturity and limit tracking are recalculated). Continue?',
            ),
          )
        }
      />
    );
  };

  // ─── The table's per-location switches ───
  // Each saves that one setting straight away. The Function, the temperature
  // limit and differentials restart the location's tracking, so a live pour
  // asks first; evaporation does not.

  /** Temperature or Maturity. Maturity counts from the
   *  concrete start, so a location switched to it without one starts
   *  counting from its monitoring start. */
  const saveRowFunction = (loc: PourLocation2, fn: LocationFunctionValue) => {
    const patch: Partial<PourLocation2Create> = { ...locationFunctionFields(fn) };
    if (fn === 'maturity' && !loc.EAStart) {
      const start = loc.LogginStart || loc.LoggingStarted;
      if (start) patch.EAStart = start;
    }
    saveRowPatch(loc, patch, true);
  };

  const saveRowTempLimit = (loc: PourLocation2, on: boolean) =>
    saveRowPatch(
      loc,
      { has_temp_limit: on },
      true,
      t(
        'pourLocations.restartConfirmLimits',
        'This pour is in progress. Changing the temperature limit restarts tracking for this location (maturity and limit tracking are recalculated). Continue?',
      ),
    );

  const saveRowEvaporation = (loc: PourLocation2, on: boolean) =>
    saveRowPatch(loc, { hasWeather: on }, false);

  /** Concrete hardware picks its Function; site hardware's comes from the
   *  device. */
  const rowChoosesFunction = (loc: PourLocation2): boolean => {
    const hw = rowHardware(loc);
    return hw !== 'Weather Station' && hw !== 'Temperature & Humidity' &&
      (loc.channel_type || '').toUpperCase() !== 'HUMIDITY';
  };

  // ─── Silent multi-sensor expansion (ConXenviro) — mirrors PourCreate ───
  // A ConXenviro carries several sensors (temp probe + SHT40 temp/humidity).
  // The dialog stays a simple pick-device-and-name form; handleSave expands
  // the single entry into one location per real channel automatically.
  const dialogLogger = lookups.loggers.find((l) => l.id === formState.LoggerID);
  const channelTypeLabel = (typ?: string | null): string =>
    typ === 'HUMIDITY' ? 'Humidity' : 'Temperature';
  /** Real channels an enviro Add expands into: populated + typed and not
   *  already bound by another location on the same logger. Empty for
   *  non-enviro loggers or while editing a single existing location. */
  const enviroExpandChannels =
    !editingLocation &&
    hwType === 'Temperature & Humidity' &&
    dialogLogger?.type === '10'
      ? (dialogLogger.channels ?? []).filter(
          (c) =>
            c.populated &&
            c.type &&
            !locations.some(
              (loc) => loc.LoggerID === formState.LoggerID && loc.ChannelNo === c.no,
            ),
        )
      : [];

  /** The location names an enviro Add will create for a given base name —
   *  shared by the dialog preview and handleSave so they always agree. */
  const enviroExpandNames = (base: string): { no: number; name: string }[] => {
    const typeCounts = enviroExpandChannels.reduce<Record<string, number>>((acc, c) => {
      const label = channelTypeLabel(c.type);
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {});
    return enviroExpandChannels.map((c) => {
      const label = channelTypeLabel(c.type);
      const suffix = typeCounts[label] > 1 ? `${label} (Ch ${c.no})` : label;
      return { no: c.no, name: `${base} ${suffix}` };
    });
  };

  const getMonitorTypeLabel = (loc: PourLocation2): string =>
    loc.MonitorType || loc.IsAmbient
      ? locationFunctionLabel(locationFunctionOf(loc.MonitorType, loc.IsAmbient))
      : '-';

  const isTargetsDisabled = !maturityChecked;

  // Picker plumbing: formState holds UTC; the pickers show project-local time.
  const pickerValue = (utc: string): Dayjs | null => {
    if (!utc) return null;
    const local = utcToLocalInput(utc, projectTimezoneIana);
    return local ? dayjs(local) : null;
  };
  const handlePicker = (field: 'LogginStart' | 'EAStart' | 'LoggingEnd') => (val: Dayjs | null) =>
    handleFormChange(field, val ? localInputToUtc(val.format('YYYY-MM-DDTHH:mm'), projectTimezoneIana) : '');

  // Times are shown in the project's (site's) timezone; the zone name goes
  // alongside so nobody reads them as their own local time.
  const formatDateTime = (value?: string | null): string =>
    value
      ? `${formatInTimeZone(value, projectTimezoneIana)} ${timeZoneLabel(value, projectTimezoneIana)}`
      : '-';
  /** Picker label with the zone the time is entered in, e.g. "Start (AEST)". */
  const tzLabelled = (label: string, value?: string | null): string =>
    `${label} (${timeZoneLabel(value, projectTimezoneIana)})`;

  // =====================================================================
  // Validation
  // =====================================================================

  // The dialog's validation list: nothing before the first Save, then
  // worked out again on every change, so a problem the user fixes drops off
  // (and a new one shows) without another Save. A server error shows beside
  // it as before.
  const shownDialogErrors = dialogOpen && dialogErrorsShown ? dialogProblems() : [];

  // =====================================================================
  // Row helpers
  // =====================================================================

  const rowHardware = (row: PourLocation2): HardwareType =>
    hwFromLoggerType(loggerTypeById.get(row.LoggerID), row.TankID);

  const rowFlags = (row: PourLocation2): string[] => {
    const flags: string[] = [];
    if (row.MonitorType === '1') flags.push('Maturity');
    if (row.TankID) flags.push('TMC');
    if (row.hasWeather) flags.push('Evaporation');
    if (row.has_temp_limit) flags.push('Temp Limits');
    // With a differential limit the card lists the pairs on their own line.
    if (row.hasTempDiffLimit && !hasTempDiffLimitDefault) flags.push('Differential');
    return flags;
  };

  const anyTank = locations.some((l) => Boolean(l.TankID));

  // The table lists each device once, with its locations (channels) under
  // it in channel order; devices go in hardware-number order.
  const deviceGroups = useMemo(() => {
    const groups = new Map<string, { loggerId: string; hw: HardwareType; deviceName: string; rows: PourLocation2[] }>();
    locations.forEach((loc) => {
      let group = groups.get(loc.LoggerID);
      if (!group) {
        const logger = lookups.loggers.find((l) => l.id === loc.LoggerID);
        group = {
          loggerId: loc.LoggerID,
          hw: hwFromLoggerType(logger?.type, loc.TankID),
          deviceName: loc.LoggerCode || loggerDisplayName(logger) || loc.LoggerID,
          rows: [],
        };
        groups.set(loc.LoggerID, group);
      }
      group.rows.push(loc);
    });
    const list = Array.from(groups.values());
    list.forEach((g) => g.rows.sort((x, y) => Number(x.ChannelNo) - Number(y.ChannelNo)));
    return list.sort((x, y) => x.deviceName.localeCompare(y.deviceName, undefined, { numeric: true }));
  }, [locations, lookups.loggers]);
  // Concrete Start (EAStart) only applies to maturity locations.
  const anyMaturity = locations.some((l) => l.MonitorType === MONITOR_TYPE_EA);
  const editableTable = !isViewMode;
  // Name, Ch, Function, Start and End, plus the optional columns; the
  // device, loading and empty rows span them all.
  const tableColumnCount =
    5 +
    (isViewMode ? 0 : 2) +
    (anyMaturity ? 1 : 0) +
    (hasTempLimitDefault ? 1 : 0) +
    (hasWeatherStation ? 1 : 0) +
    (anyTank ? 1 : 0) +
    (hasTempDiffLimitDefault ? 1 : 0);
  const dateCellSx = editableTable
    ? { ...TABLE_TIME_CELL_SX, cursor: 'pointer', '&:hover': { color: 'primary.main' } }
    : TABLE_TIME_CELL_SX;

  // =====================================================================
  // Render
  // =====================================================================

  return (
    <Box>
      {error && !dialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {!isViewMode && stationLocations.length > 0 && pourFunctions.length > 0 && !usesWeather && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t(
            'pourLocations.stationNotLinked',
            'A weather station sends readings to a pour only when Onsite Weather Conditions or Concrete Evaporation Rate is turned on. Turn one on in the pour details and save.',
          )}
        </Alert>
      )}
      {usesWeather && stationLocations.length > 1 && linkedStation && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'pourLocations.oneStationFeeds',
            "Only {{name}} sends weather to this pour. The dashboard shows the other weather stations as not linked.",
            { name: stationName(linkedStation) },
          )}
        </Alert>
      )}

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : locations.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}>
              <Typography variant="body2" color="text.secondary">
                No locations added yet. Tap &ldquo;Create a new location&rdquo; below to add a monitoring location.
              </Typography>
            </Paper>
          ) : (
            locations.map((loc) => {
              const flags = rowFlags(loc);
              return (
                <Card key={loc.ID} elevation={1}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                          {loc.LocationID || t('pourLocations.unnamedLocation', 'Unnamed Location')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                          <Chip label={rowHardware(loc)} size="small" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            {loc.LoggerCode || getLoggerName(loc.LoggerID)}
                            {` · ch.${loc.ChannelNo}`}
                          </Typography>
                        </Box>
                      </Box>
                      {!isViewMode && (
                        <Box sx={{ flexShrink: 0 }}>
                          <IconButton size="small" onClick={() => handleOpenEdit(loc)} title="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(loc)} title="Delete">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5, mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Start</Typography>
                      <Typography variant="caption">{formatDateTime(loc.LogginStart || loc.LoggingStarted)}</Typography>
                      {loc.MonitorType === MONITOR_TYPE_EA && (
                        <>
                          <Typography variant="caption" color="text.secondary" title={concreteStartHelp}>
                            {concreteStartLabel}
                          </Typography>
                          <Typography variant="caption">{formatDateTime(loc.EAStart)}</Typography>
                        </>
                      )}
                      <Typography variant="caption" color="text.secondary">End</Typography>
                      <Typography variant="caption">{formatDateTime(loc.LoggingEnd || loc.LoggingFinished)}</Typography>
                      {loc.TankID && (
                        <>
                          <Typography variant="caption" color="text.secondary">Tank</Typography>
                          <Typography variant="caption">{getTankName(loc.TankID)}</Typography>
                        </>
                      )}
                      {hasTempDiffLimitDefault && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '20px' }}>
                            Differentials
                          </Typography>
                          {renderDiffCell(loc, 'inline')}
                        </>
                      )}
                    </Box>
                    {flags.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {flags.map((f) => (
                          <Chip key={f} label={f} size="small" sx={{ height: 20, fontSize: '0.68rem' }} />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
          <Table size="small" sx={{ '& tbody td': { py: 1.25 }, '& td, & th': { px: 1 } }}>
            <TableHead>
              <TableRow sx={{ '& th': { color: 'text.secondary', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' } }}>
                {!isViewMode && <TableCell sx={TABLE_ICON_CELL_SX} />}
                <TableCell>Location Name</TableCell>
                <TableCell align="center">Ch</TableCell>
                <TableCell>Function</TableCell>
                <TableCell>Start</TableCell>
                {anyMaturity && (
                  <TableCell title={`${concreteStartLabel}. ${concreteStartHelp}`}>{CONCRETE_START_SHORT_LABEL}</TableCell>
                )}
                <TableCell>End</TableCell>
                {hasTempLimitDefault && <TableCell align="center" sx={TABLE_TICK_HEAD_SX}>Temp Limit</TableCell>}
                {hasWeatherStation && <TableCell align="center" sx={TABLE_TICK_HEAD_SX}>Evaporation</TableCell>}
                {anyTank && <TableCell>Tank</TableCell>}
                {hasTempDiffLimitDefault && <TableCell>Differentials</TableCell>}
                {!isViewMode && <TableCell sx={TABLE_ICON_CELL_SX} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={tableColumnCount} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableColumnCount} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No hardware added yet. Click &ldquo;Add hardware&rdquo; below to add a monitoring location.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                deviceGroups.map((group) => [
                  <TableRow key={`device-${group.loggerId}`} data-testid={`device-group-${group.loggerId}`}>
                    <TableCell colSpan={tableColumnCount} sx={{ bgcolor: 'grey.50', py: '10px !important' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <HardwareTypeImage type={group.hw} size={40} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {HARDWARE_TYPE_NAMES[group.hw]} · {group.deviceName}
                          </Typography>
                          {group.hw === 'ConXagent' && (
                            <Typography variant="caption" color="text.secondary">
                              {group.rows.length} of 4 channels on this pour
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>,
                  ...group.rows.map((loc) => {
                    const hw = rowHardware(loc);
                    const rowBusy = rowSavingId === loc.ID;
                    const isMaturityRow = loc.MonitorType === MONITOR_TYPE_EA;
                    const concrete = isConcreteLocation(loc);
                    const openDate = (field: 'LogginStart' | 'EAStart' | 'LoggingEnd') =>
                      (e: React.MouseEvent<HTMLElement>) => {
                        if (!editableTable || rowBusy) return;
                        setDatePopover({ anchorEl: e.currentTarget, loc, field, value: pickerValue(loc[field] ?? '') });
                      };
                    const tankOptions = [...(lookups.tanks ?? [])]
                      .filter((tk) => !tk.in_use_by_pour || tk.in_use_by_pour === pourId || tk.id === loc.TankID)
                      .sort((a, b) => a.name.localeCompare(b.name));
                    // Channels of this device held by the pour's other
                    // locations: each channel can only be allocated once.
                    const takenOnDevice = editableTable
                      ? new Set(
                          locations
                            .filter((o) => o.ID !== loc.ID && o.LoggerID === loc.LoggerID)
                            .map((o) => Number(o.ChannelNo)),
                        )
                      : new Set<number>();
                    const fixedFunction = locationFunctionView(hw, maturityAllowed);
                    return (
                      <TableRow key={loc.ID} sx={rowBusy ? { opacity: 0.6 } : undefined}>
                        {!isViewMode && (
                          <TableCell sx={TABLE_ICON_CELL_SX}>
                            <IconButton size="small" onClick={() => handleOpenEdit(loc)} title="Edit full details" disabled={rowBusy}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                        <TableCell sx={TABLE_NAME_CELL_SX}>
                          {editableTable ? (
                            <TextField
                              value={nameDrafts[loc.ID] ?? loc.LocationID ?? ''}
                              onChange={(e) => setNameDrafts((prev) => ({ ...prev, [loc.ID]: e.target.value }))}
                              onBlur={() => commitNameDraft(loc)}
                              onKeyDown={(e) => {
                                // Enter saves the name; it never adds a line.
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  (e.target as HTMLInputElement).blur();
                                }
                                if (e.key === 'Escape') dropNameDraft(loc.ID);
                              }}
                              size="small"
                              variant="standard"
                              placeholder="Location name"
                              fullWidth
                              multiline
                              maxRows={3}
                              disabled={rowBusy}
                              inputProps={{ maxLength: 50, style: { fontSize: '0.85rem', fontWeight: 600 } }}
                            />
                          ) : (
                            loc.LocationID || '-'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {editableTable && HW_HAS_CHANNEL[hw] && !HW_SINGLE_CHANNEL[hw] ? (
                            <TextField
                              value={loc.ChannelNo}
                              onChange={(e) => {
                                const ch = Number(e.target.value);
                                if (ch !== loc.ChannelNo) saveRowPatch(loc, { ChannelNo: ch }, true);
                              }}
                              select
                              size="small"
                              variant="standard"
                              sx={{ width: 40 }}
                              disabled={rowBusy}
                              SelectProps={{ style: { fontSize: '0.85rem' } }}
                            >
                              {Array.from(new Set([1, 2, 3, 4, loc.ChannelNo])).map((ch) => (
                                <MenuItem key={ch} value={ch} disabled={ch !== loc.ChannelNo && takenOnDevice.has(ch)}>
                                  {ch}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : HW_HAS_CHANNEL[hw] ? (
                            loc.ChannelNo
                          ) : (
                            NOT_APPLICABLE
                          )}
                        </TableCell>
                        <TableCell>
                          {editableTable && rowChoosesFunction(loc) ? (
                            <LocationFunctionToggle
                              value={locationFunctionOf(loc.MonitorType, loc.IsAmbient)}
                              onChange={(fn) => saveRowFunction(loc, fn)}
                              maturityAllowed={maturityAllowed}
                              maturityName={functionLabel}
                              disabled={rowBusy}
                              ariaLabel={`${loc.LocationID || 'Location'} function`}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {!rowChoosesFunction(loc) && fixedFunction.kind === 'fixed'
                                ? fixedFunction.label
                                : getMonitorTypeLabel(loc)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={dateCellSx} onClick={openDate('LogginStart')}>
                          {formatDateTime(loc.LogginStart || loc.LoggingStarted)}
                        </TableCell>
                        {anyMaturity && (
                          <TableCell
                            sx={isMaturityRow ? dateCellSx : TABLE_TIME_CELL_SX}
                            onClick={isMaturityRow ? openDate('EAStart') : undefined}
                          >
                            {isMaturityRow ? formatDateTime(loc.EAStart) : NOT_APPLICABLE}
                          </TableCell>
                        )}
                        <TableCell sx={dateCellSx} onClick={openDate('LoggingEnd')}>
                          {formatDateTime(loc.LoggingEnd || loc.LoggingFinished)}
                        </TableCell>
                        {hasTempLimitDefault && (
                          <TableCell align="center">
                            {concrete ? (
                              <Checkbox
                                size="small"
                                checked={Boolean(loc.has_temp_limit)}
                                onChange={(e) => saveRowTempLimit(loc, e.target.checked)}
                                disabled={!editableTable || rowBusy}
                                inputProps={{ 'aria-label': `Temperature limit for ${loc.LocationID || 'location'}` }}
                                sx={{ p: 0.5, '&.Mui-checked': { color: BRAND_GREEN } }}
                              />
                            ) : (
                              NOT_APPLICABLE
                            )}
                          </TableCell>
                        )}
                        {hasWeatherStation && (
                          <TableCell align="center">
                            {concrete ? (
                              <Checkbox
                                size="small"
                                checked={Boolean(loc.hasWeather)}
                                onChange={(e) => saveRowEvaporation(loc, e.target.checked)}
                                disabled={!editableTable || rowBusy}
                                inputProps={{ 'aria-label': `Evaporation rate for ${loc.LocationID || 'location'}` }}
                                sx={{ p: 0.5, '&.Mui-checked': { color: BRAND_GREEN } }}
                              />
                            ) : (
                              NOT_APPLICABLE
                            )}
                          </TableCell>
                        )}
                        {anyTank && (
                          <TableCell>
                            {editableTable && loc.TankID ? (
                              <TextField
                                value={loc.TankID}
                                onChange={(e) => {
                                  if (e.target.value !== loc.TankID) saveRowPatch(loc, { TankID: e.target.value }, false);
                                }}
                                select
                                size="small"
                                variant="standard"
                                fullWidth
                                disabled={rowBusy}
                                SelectProps={{ style: { fontSize: '0.85rem' } }}
                              >
                                {!tankOptions.some((tk) => tk.id === loc.TankID) && (
                                  <MenuItem value={loc.TankID}>{loc.TankID}</MenuItem>
                                )}
                                {tankOptions.map((tk) => (
                                  <MenuItem key={tk.id} value={tk.id}>{tk.name}</MenuItem>
                                ))}
                              </TextField>
                            ) : loc.TankID ? (
                              getTankName(loc.TankID)
                            ) : (
                              NOT_APPLICABLE
                            )}
                          </TableCell>
                        )}
                        {hasTempDiffLimitDefault && (
                          <TableCell sx={{ minWidth: 120, maxWidth: 190 }}>{renderDiffCell(loc, 'cell')}</TableCell>
                        )}
                        {!isViewMode && (
                          <TableCell sx={TABLE_ICON_CELL_SX}>
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(loc)} disabled={rowBusy} title="Remove location">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  }),
                ])
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Inline date picker: opens straight from a Start / Concrete Start /
          End cell. It stays open while the day and time are picked
          (closeOnSelect off) and clicking away saves that one field. */}
      {datePopover && (
        <DateTimePicker
          open
          onClose={() => setDatePopover(null)}
          value={datePopover.value}
          onChange={(val: Dayjs | null) => setDatePopover((prev) => (prev ? { ...prev, value: val } : prev))}
          onAccept={commitDate}
          closeOnSelect={false}
          minutesStep={5}
          ampm={false}
          slotProps={{
            textField: {
              sx: { position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0, pointerEvents: 'none' },
            },
            actionBar: { sx: { display: 'none' } },
            popper: { anchorEl: datePopover.anchorEl, sx: PICKER_POPPER_SX },
          }}
        />
      )}

      {!isViewMode && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
          disabled={loading}
          fullWidth={isMobile}
          sx={{ textTransform: 'none', ...(isMobile && { py: 1.25 }) }}
        >
          Add hardware
        </Button>
      )}

      {/* ============================================================= */}
      {/* Add / Edit dialog: where a sensor is (hardware, channel, name, */}
      {/* function, times). Limits, differentials and evaporation are    */}
      {/* switched on in the table.                                      */}
      {/* ============================================================= */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          'aria-label': editingLocation ? 'Edit hardware' : 'Add hardware',
          sx: isMobile
            ? { display: 'flex', flexDirection: 'column' }
            : {
                width: 560,
                maxWidth: 'calc(100% - 32px)',
                height: 'min(880px, calc(100% - 64px))',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
              },
        }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Hardware &amp; sensors
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.3 }}>
              {editingLocation
                ? `Edit ${editingLocation.LocationID || 'location'}`
                : 'Add hardware'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} edge="end" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box ref={dialogContentRef} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          {addNotice && (
            <Alert severity="success" sx={{ mb: 1.5, py: 0 }} onClose={() => setAddNotice(null)}>
              {addNotice}
            </Alert>
          )}
          {(shownDialogErrors.length > 0 || error) && (
            <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>
              {shownDialogErrors.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
              {error && <div>{error}</div>}
            </Alert>
          )}

          {/* Only the types the pour's monitoring functions support. An
              edit keeps its type: remove the location and add it again to
              change hardware type. */}
          <DialogStep n={1} title="Hardware type">
            <HardwareTypeCards
              options={hardwareOptions}
              value={hwType}
              disabled={!!editingLocation}
              onChange={(value) => {
                setHwType(value as HardwareType);
                setFormState((prev) => ({ ...prev, LoggerID: '', ChannelNo: '', tempDiffLocationIds: [] }));
              }}
            />
          </DialogStep>

          {/* Hardware Number: type to search ("734"), Enter picks it */}
          <DialogStep n={2} title="Hardware number">
            <HardwareNumberPicker
              options={deviceOptions}
              value={formState.LoggerID}
              onChange={handleDeviceChange}
              disabled={!hwType}
              required
              inputRef={deviceInputRef}
            />
          </DialogStep>

          <DialogStep n={3} title={multiMode ? 'Channels & locations' : 'Location'}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* ConXagent add: every channel at once, a name and a
                  Function each */}
              {multiMode && (
                <ChannelRowsPicker
                  {...batch.pickerProps}
                  pourDiffOptions={pourDiffOptions}
                  onEnter={() => handleSave(true)}
                  disabled={saving}
                  firstNameInputRef={firstNameInputRef}
                  functionOptions={functionView.kind === 'choice' ? functionView.options : undefined}
                  maturityName={functionLabel}
                />
              )}

              {/* Hardware Channel Number (not for single-channel devices) */}
              {channelVisible && !multiMode && !HW_SINGLE_CHANNEL[hwType] && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Hardware Channel Number
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {[1, 2, 3, 4].map((ch) => {
                      const active = formState.ChannelNo === String(ch);
                      const usedByOther = formState.LoggerID && locations.some(
                        (loc) =>
                          loc.LoggerID === formState.LoggerID &&
                          loc.ChannelNo === ch &&
                          loc.ID !== editingLocation?.ID,
                      );
                      return (
                        <Box
                          key={ch}
                          onClick={() => {
                            if (!usedByOther) handleFormChange('ChannelNo', String(ch));
                          }}
                          sx={{
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid',
                            borderColor: usedByOther ? 'grey.200' : active ? BRAND_GREEN : 'grey.300',
                            borderRadius: '6px',
                            backgroundColor: usedByOther ? 'grey.100' : active ? BRAND_GREEN : 'transparent',
                            color: usedByOther ? 'grey.400' : active ? '#1a1a2e' : 'text.primary',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            cursor: usedByOther ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {ch}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Location Name */}
              {!multiMode && (
                <TextField
                  label="Location Name"
                  size="small"
                  value={formState.LocationID}
                  onChange={(e) => handleFormChange('LocationID', e.target.value)}
                  fullWidth
                  required
                  inputProps={{ maxLength: 50 }}
                  helperText={
                    hwType === 'Temperature & Humidity' ? undefined : 'e.g. Middle, Bottom, Surface, Ambient'
                  }
                  FormHelperTextProps={{ sx: { ml: 0, mt: 0.25, fontSize: '0.7rem' } }}
                />
              )}

              {/* Preview of the locations a T&H sensor will create — shown from
                  the moment the dialog opens (generic pattern), refined to the
                  exact channel list once a device is selected, live-updated as
                  the user types the name. Same block as PourCreate. */}
              {!editingLocation &&
                hwType === 'Temperature & Humidity' &&
                (enviroExpandChannels.length > 0 || !formState.LoggerID) && (
                <Box
                  sx={{
                    backgroundColor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {enviroExpandChannels.length > 0
                      ? `This sensor records ${enviroExpandChannels.length} readings, saving creates:`
                      : 'One location is created per sensor reading, for example:'}
                  </Typography>
                  {(enviroExpandChannels.length > 0
                    ? enviroExpandNames(formState.LocationID.trim() || 'Location Name')
                    : [
                        { no: -1, name: `${formState.LocationID.trim() || 'Location Name'} Temperature` },
                        { no: -2, name: `${formState.LocationID.trim() || 'Location Name'} Humidity` },
                      ]
                  ).map(({ no, name }) => (
                    <Typography
                      key={no}
                      variant="body2"
                      sx={{
                        fontSize: '0.8rem',
                        color:
                          formState.LocationID.trim() && enviroExpandChannels.length > 0
                            ? 'text.primary'
                            : 'text.disabled',
                        lineHeight: 1.7,
                      }}
                    >
                      • {name}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* Function: concrete hardware picks Temperature or Temperature +
                  Maturity (a ConXagent picks per channel, in its rows); site
                  hardware's follows from the device. */}
              {hwType && (functionView.kind === 'fixed' || !multiMode) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Function
                  </Typography>
                  {functionView.kind === 'fixed' ? (
                    <Typography variant="body2">{functionView.label}</Typography>
                  ) : (
                    <LocationFunctionToggle
                      value={locationFunctionOf(formState.MonitorType, formState.IsAmbient)}
                      maturityAllowed={maturityAllowed}
                      maturityName={functionLabel}
                      onChange={(fn) => {
                        setFormState((prev) => ({
                          ...prev,
                          ...locationFunctionFields(fn),
                          EAStart: fn === 'maturity' ? prev.EAStart || prev.LogginStart : '',
                        }));
                      }}
                    />
                  )}
                </Box>
              )}

              {/* Tank (only for TMC) */}
              {hwType === 'TMC' && sortedTanks.length > 0 && (
                <Autocomplete
                  options={sortedTanks}
                  getOptionLabel={(option) => option.name}
                  value={sortedTanks.find((tk) => tk.id === formState.TankID) || null}
                  onChange={(_, newValue) => handleFormChange('TankID', newValue?.id || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Tank" size="small" required />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  autoHighlight
                  openOnFocus
                  selectOnFocus
                  handleHomeEndKeys
                  fullWidth
                />
              )}
            </Box>
          </DialogStep>

          <DialogStep n={4} title="Times">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
              <DateTimePicker
                label={tzLabelled('Start time', formState.LogginStart)}
                value={pickerValue(formState.LogginStart)}
                onChange={handlePicker('LogginStart')}
                minutesStep={5}
                ampm={false}
                slotProps={{
                  textField: { fullWidth: true, size: 'small', required: true },
                  actionBar: { sx: { display: 'none' } },
                  popper: { sx: PICKER_POPPER_SX },
                }}
              />
              {concreteStartVisible && (
                // The full name and what it does are the field's tooltip.
                <Box title={`${concreteStartLabel}. ${concreteStartHelp}`}>
                  <DateTimePicker
                    label={tzLabelled(CONCRETE_START_SHORT_LABEL, formState.EAStart)}
                    value={pickerValue(formState.EAStart)}
                    onChange={handlePicker('EAStart')}
                    minutesStep={5}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        helperText: 'Maturity counts from this time.',
                        sx: { '& .MuiFormHelperText-root': { ml: 0, mt: 0.25, fontSize: '0.7rem' } },
                      },
                      actionBar: { sx: { display: 'none' } },
                      popper: { sx: PICKER_POPPER_SX },
                    }}
                  />
                </Box>
              )}
              <DateTimePicker
                label={tzLabelled('Stop time', formState.LoggingEnd)}
                value={pickerValue(formState.LoggingEnd)}
                onChange={handlePicker('LoggingEnd')}
                minutesStep={5}
                ampm={false}
                slotProps={{
                  textField: { fullWidth: true, size: 'small' },
                  actionBar: { sx: { display: 'none' } },
                  popper: { sx: PICKER_POPPER_SX },
                }}
              />
              {editingLocation && pourRunning && (
                <Alert severity="warning" sx={{ py: 0, gridColumn: '1 / -1' }}>
                  This pour is in progress. Changing the hardware, channel, function or a time restarts tracking for this location.
                </Alert>
              )}
            </Box>
          </DialogStep>

          {/* Target Strengths — only if pour has targets */}
          {hasTargetGroup && pourTargets.length > 0 && (
            <DialogStep n={5} title="Target strengths">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {pourTargets.map((target) => (
                  <TextField
                    key={target.ID}
                    label={target.Purpose}
                    type="number"
                    size="small"
                    value={formState.targetStrengths[target.ID] || ''}
                    onChange={(e) => handleTargetStrengthChange(target.ID, e.target.value)}
                    fullWidth
                    disabled={isTargetsDisabled}
                    InputProps={{
                      // The company unit; none while it is unknown.
                      endAdornment: strengthUnit?.trim()
                        ? <InputAdornment position="end">{strengthUnit.trim()}</InputAdornment>
                        : undefined,
                    }}
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        backgroundColor: 'grey.100',
                      },
                    }}
                  />
                ))}
                {isTargetsDisabled && (
                  <Typography variant="caption" color="text.secondary">
                    {t('pourLocations.targetsDisabledHint', 'Target strengths only apply to Maturity locations')}
                  </Typography>
                )}
              </Box>
            </DialogStep>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderTop: '1px solid', borderColor: 'grey.200', pt: 1.5 }}>
            Temperature limits, differentials and evaporation are switched on per location in the table after saving.
          </Typography>
        </Box>
        {/* Buttons wrap, and stack full width on a phone. */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'grey.200',
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1,
            ...(isMobile && { flexDirection: 'column-reverse', alignItems: 'stretch' }),
          }}
        >
          {editingLocation && (
            <Button
              onClick={() => handleDeleteClick(editingLocation)}
              variant="text"
              color="error"
              disabled={saving}
              fullWidth={isMobile}
              startIcon={<DeleteIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 500, mr: isMobile ? 0 : 'auto' }}
            >
              Delete
            </Button>
          )}
          <Button
            onClick={handleCloseDialog}
            variant="text"
            disabled={saving}
            fullWidth={isMobile}
            sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
          >
            {addedCount > 0 ? 'Close' : 'Cancel'}
          </Button>
          {!editingLocation && (
            <Button
              onClick={() => handleSave(true)}
              variant="outlined"
              disabled={saving}
              fullWidth={isMobile}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Save &amp; add next device
            </Button>
          )}
          <Button
            onClick={() => handleSave(false)}
            variant="contained"
            disabled={saving}
            fullWidth={isMobile}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', px: 4, fontWeight: 600 }}
          >
            Save
          </Button>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('pourLocations.confirmDelete', 'Confirm Delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('pourLocations.confirmDeleteMessage', 'Are you sure you want to delete this location? This will also remove all tracking data for this logger/channel.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {t('pourLocations.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {t('pourLocations.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PourLocationsSection;
